import { useMemo, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, X } from "lucide-react";

import { apiFetch, type ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { Input, Label } from "@/components/ui/Input";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintContent, HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { formatNumber } from "@/lib/mrp";

export interface TemplateOption {
  label: string;
  value: number;
}

export interface RevisionOption {
  label: string;
  value: number;
  description: string | null;
  approved_at: string | null;
  submitted_at: string | null;
}

export interface StaffOption {
  value: number;
  label: string;
  email: string | null;
}

export function useBomMe() {
  return useQuery({
    queryKey: ["bom", "me"],
    queryFn: () => apiFetch<{ staff: { id: number; fullName: string | null; email: string | null } | null; email: string }>(
      "/api/bom/me",
    ),
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });
}

export function useBomStaff() {
  return useQuery({
    queryKey: ["bom", "staff"],
    queryFn: () => apiFetch<{ staff: StaffOption[] }>("/api/bom/staff"),
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });
}

type UnknownRow = Record<string, unknown>;

function pickVariableNames(rows: UnknownRow[] | undefined, key: string): Set<string> {
  const set = new Set<string>();
  for (const row of rows ?? []) {
    const v = row[key];
    if (typeof v === "string" && v.length > 0) {
      set.add(v);
    }
  }
  return set;
}

/**
 * Initial rows that have no matching variable_name in the specific BoM (same check as Appsmith filter.findUnusedBomInitial).
 */
export function findUnusedInitial(initial: UnknownRow[] | undefined, specific: UnknownRow[] | undefined): UnknownRow[] {
  const inSpecific = pickVariableNames(specific, "variable_name");
  return (initial ?? []).filter((row) => {
    const vn = row.variable_name;
    if (typeof vn !== "string" || vn.length === 0) {
      return true;
    }
    return !inSpecific.has(vn);
  });
}

const DIFF_CELL = "bg-amber-100/90";
const ROW_ONLY_LEFT = "bg-rose-50/90";
const ROW_ONLY_RIGHT = "bg-emerald-50/90";

type BomCompareCol = "component_sku" | "quantity" | "variable_name" | "full_description";

function formatCellRaw(v: unknown): string {
  if (v === null || v === undefined) {
    return "—";
  }
  if (typeof v === "object") {
    return JSON.stringify(v);
  }
  if (typeof v === "number") {
    return formatNumber(v);
  }
  return String(v);
}

function cellValuesEqualParse(a: unknown, b: unknown): boolean {
  return formatCellRaw(a) === formatCellRaw(b);
}

function quantitiesEqual(a: unknown, b: unknown): boolean {
  const na = typeof a === "number" ? a : Number(a);
  const nb = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) {
    return na === nb;
  }
  return formatCellRaw(a) === formatCellRaw(b);
}

/** Stable variable slot key for matching BoM lines across revisions. */
function bomRowKey(row: Record<string, unknown>): string {
  const vn = row.variable_name;
  if (typeof vn === "string" && vn.length > 0) {
    return vn;
  }
  return `__no_variable__:${String(row.component_sku ?? "")}:${String(row.id ?? "")}`;
}

/** PostgreSQL often exposes function results under this column name. */
const PARSE_JSON_COLUMN_CANDIDATES = ["parse_sku_specific", "parse_sku_current", "result"] as const;

const BOM_ALIGN_PLACEHOLDER = "_bomAlignPlaceholder";

/** Keys to hide in review template-definition JSON tables (context already names template; omit raw station id where name is shown). */
export const BOM_DEFINITION_JSON_OMIT_KEYS = ["template_id", "revision", "id", "kit_to_station_id"];

export function extractParseFeatureMap(rows: unknown[] | undefined): Record<string, unknown> | null {
  if (!rows?.length) {
    return null;
  }
  const row = rows[0];
  if (!row || typeof row !== "object" || Array.isArray(row)) {
    return null;
  }
  const rec = row as Record<string, unknown>;
  for (const column of PARSE_JSON_COLUMN_CANDIDATES) {
    const val = rec[column];
    if (val != null && typeof val === "object" && !Array.isArray(val)) {
      return val as Record<string, unknown>;
    }
  }
  const keys = Object.keys(rec);
  if (keys.length === 1) {
    const only = rec[keys[0]];
    if (only != null && typeof only === "object" && !Array.isArray(only)) {
      return only as Record<string, unknown>;
    }
  }
  return null;
}

/** Trailing SKU segment left over when parse_sku_specific runs in safe mode. */
export function extractRemainingSkuTrimmed(rows: unknown[] | undefined): string | null {
  const parsed = extractParseFeatureMap(rows);
  if (!parsed) {
    return null;
  }
  const raw = parsed.remainingSku;
  if (typeof raw !== "string" || raw.trim().length === 0) {
    return null;
  }
  return raw.trim();
}

/**
 * create_bom_specific surfaces the same "unprocessed characters" failure as safe parse's remainingSku.
 * Hiding this avoids a second box; the incomplete-parse callout already covers trailing SKU text.
 */
function shouldSuppressRedundantBomPreviewError(
  parseRows: unknown[] | undefined,
  bomErrorMessage: string | null | undefined,
): boolean {
  if (!bomErrorMessage) {
    return false;
  }
  const remaining = extractRemainingSkuTrimmed(parseRows);
  if (!remaining) {
    return false;
  }
  return /Unprocessed characters left in SKU/i.test(bomErrorMessage);
}

function IncompleteParseCallout(props: { remaining: string; comfortable: boolean }): React.ReactElement {
  const comfortable = props.comfortable;
  return (
    <p
      className={
        comfortable
          ? "rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-base text-amber-950"
          : "rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
      }
    >
      <span className="font-medium">Incomplete parse for this revision: </span>
      these trailing characters were not consumed by the parse rules:{" "}
      <code className="rounded bg-amber-100/80 px-1 font-mono text-xs">{props.remaining}</code>
    </p>
  );
}

export function formatParseFeatureValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function bomVariableSortKey(row: Record<string, unknown>): string {
  const vn = row.variable_name;
  if (typeof vn === "string" && vn.length > 0) {
    return vn;
  }
  return bomRowKey(row);
}

function alignBomRowsForComparison(
  left: Record<string, unknown>[] | undefined,
  right: Record<string, unknown>[] | undefined,
): { left: Record<string, unknown>[]; right: Record<string, unknown>[] } {
  const mapLeft = new Map<string, Record<string, unknown>>();
  const mapRight = new Map<string, Record<string, unknown>>();
  for (const row of left ?? []) {
    mapLeft.set(bomVariableSortKey(row), row);
  }
  for (const row of right ?? []) {
    mapRight.set(bomVariableSortKey(row), row);
  }
  const keys = [...new Set([...mapLeft.keys(), ...mapRight.keys()])].sort((a, b) => a.localeCompare(b));
  const leftOut = keys.map((key) => {
    const row = mapLeft.get(key);
    if (row) {
      return { ...row, [BOM_ALIGN_PLACEHOLDER]: false };
    }
    return {
      variable_name: key,
      component_sku: null,
      quantity: null,
      full_description: null,
      [BOM_ALIGN_PLACEHOLDER]: true,
    };
  });
  const rightOut = keys.map((key) => {
    const row = mapRight.get(key);
    if (row) {
      return { ...row, [BOM_ALIGN_PLACEHOLDER]: false };
    }
    return {
      variable_name: key,
      component_sku: null,
      quantity: null,
      full_description: null,
      [BOM_ALIGN_PLACEHOLDER]: true,
    };
  });
  return { left: leftOut, right: rightOut };
}

function buildAlignedBomHighlights(
  leftRows: Record<string, unknown>[],
  rightRows: Record<string, unknown>[],
): {
  leftTrClassName: (rowIndex: number) => string | undefined;
  leftTdClassName: (rowIndex: number, column: BomCompareCol) => string | undefined;
  rightTrClassName: (rowIndex: number) => string | undefined;
  rightTdClassName: (rowIndex: number, column: BomCompareCol) => string | undefined;
} {
  const leftTrClassName = (i: number): string | undefined => {
    const leftRow = leftRows[i];
    const rightRow = rightRows[i];
    const leftPh = leftRow?.[BOM_ALIGN_PLACEHOLDER] === true;
    const rightPh = rightRow?.[BOM_ALIGN_PLACEHOLDER] === true;
    if (!leftPh && rightPh) {
      return ROW_ONLY_LEFT;
    }
    return undefined;
  };
  const rightTrClassName = (i: number): string | undefined => {
    const leftRow = leftRows[i];
    const rightRow = rightRows[i];
    const leftPh = leftRow?.[BOM_ALIGN_PLACEHOLDER] === true;
    const rightPh = rightRow?.[BOM_ALIGN_PLACEHOLDER] === true;
    if (leftPh && !rightPh) {
      return ROW_ONLY_RIGHT;
    }
    return undefined;
  };
  const diffCells = (leftRow: Record<string, unknown>, rightRow: Record<string, unknown>): Partial<Record<BomCompareCol, boolean>> => {
    const out: Partial<Record<BomCompareCol, boolean>> = {};
    if (!quantitiesEqual(leftRow.quantity, rightRow.quantity)) {
      out.quantity = true;
    }
    if (String(leftRow.component_sku ?? "") !== String(rightRow.component_sku ?? "")) {
      out.component_sku = true;
    }
    if (String(leftRow.variable_name ?? "") !== String(rightRow.variable_name ?? "")) {
      out.variable_name = true;
    }
    if (String(leftRow.full_description ?? "") !== String(rightRow.full_description ?? "")) {
      out.full_description = true;
    }
    return out;
  };
  const leftTdClassName = (i: number, column: BomCompareCol): string | undefined => {
    const leftRow = leftRows[i];
    const rightRow = rightRows[i];
    const leftPh = leftRow?.[BOM_ALIGN_PLACEHOLDER] === true;
    const rightPh = rightRow?.[BOM_ALIGN_PLACEHOLDER] === true;
    if (!leftRow || !rightRow || leftPh || rightPh) {
      return undefined;
    }
    const d = diffCells(leftRow, rightRow);
    return d[column] ? DIFF_CELL : undefined;
  };
  const rightTdClassName = (i: number, column: BomCompareCol): string | undefined => leftTdClassName(i, column);
  return { leftTrClassName, leftTdClassName, rightTrClassName, rightTdClassName };
}

function unionParseKeys(left: unknown, right: unknown): string[] {
  const a = left && typeof left === "object" ? Object.keys(left as object) : [];
  const b = right && typeof right === "object" ? Object.keys(right as object) : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of a) {
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  for (const key of b) {
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

/**
 * Highlights for parse JsonTable: differing cells (amber), row present only on one side (rose / emerald).
 */
function buildParseCompareHighlight(
  selfRows: unknown[] | undefined,
  partnerRows: unknown[] | undefined,
  side: "left" | "right",
): { keys: string[] | undefined; cellClassName: (rowIndex: number, key: string) => string | undefined } {
  const leftRows = side === "left" ? selfRows : partnerRows;
  const rightRows = side === "left" ? partnerRows : selfRows;

  const maxR = Math.max(leftRows?.length ?? 0, rightRows?.length ?? 0);
  if (maxR === 0) {
    return { keys: undefined, cellClassName: () => undefined };
  }

  const keyUnion0 = unionParseKeys(leftRows?.[0], rightRows?.[0]);
  const keys = keyUnion0.length > 0 ? keyUnion0 : undefined;

  const cellClassName = (rowIndex: number, key: string): string | undefined => {
    const l = leftRows?.[rowIndex];
    const r = rightRows?.[rowIndex];
    const lObj = l && typeof l === "object" ? (l as Record<string, unknown>) : undefined;
    const rObj = r && typeof r === "object" ? (r as Record<string, unknown>) : undefined;

    const onlyLeft = lObj != null && rObj == null;
    const onlyRight = rObj != null && lObj == null;
    if (onlyLeft) {
      return side === "left" ? ROW_ONLY_LEFT : undefined;
    }
    if (onlyRight) {
      return side === "right" ? ROW_ONLY_RIGHT : undefined;
    }
    if (lObj == null || rObj == null) {
      return undefined;
    }
    if (!cellValuesEqualParse(lObj[key], rObj[key])) {
      return DIFF_CELL;
    }
    return undefined;
  };

  return { keys, cellClassName };
}

/**
 * Per-cell / whole-row highlights for BoM tables matched by variable_name.
 */
function buildBomCompareHighlight(
  selfRows: Record<string, unknown>[] | undefined,
  partnerRows: Record<string, unknown>[] | undefined,
  side: "left" | "right",
): {
  trClassName: (rowIndex: number) => string | undefined;
  tdClassName: (rowIndex: number, col: BomCompareCol) => string | undefined;
} {
  if (!selfRows?.length && !partnerRows?.length) {
    return { trClassName: () => undefined, tdClassName: () => undefined };
  }

  const partnerByKey = new Map<string, Record<string, unknown>>();
  for (const row of partnerRows ?? []) {
    partnerByKey.set(bomRowKey(row), row);
  }

  const trClassName = (rowIndex: number): string | undefined => {
    const row = selfRows?.[rowIndex];
    if (!row) {
      return undefined;
    }
    const key = bomRowKey(row);
    if (!partnerByKey.has(key)) {
      return side === "left" ? ROW_ONLY_LEFT : ROW_ONLY_RIGHT;
    }
    return undefined;
  };

  const tdClassName = (rowIndex: number, col: BomCompareCol): string | undefined => {
    const row = selfRows?.[rowIndex];
    if (!row) {
      return undefined;
    }
    const key = bomRowKey(row);
    const other = partnerByKey.get(key);
    if (other == null) {
      return undefined;
    }
    if (col === "quantity") {
      if (!quantitiesEqual(row.quantity, other.quantity)) {
        return DIFF_CELL;
      }
      return undefined;
    }
    if (col === "component_sku") {
      if (String(row.component_sku ?? "") !== String(other.component_sku ?? "")) {
        return DIFF_CELL;
      }
      return undefined;
    }
    if (col === "variable_name") {
      if (String(row.variable_name ?? "") !== String(other.variable_name ?? "")) {
        return DIFF_CELL;
      }
      return undefined;
    }
    if (col === "full_description") {
      if (String(row.full_description ?? "") !== String(other.full_description ?? "")) {
        return DIFF_CELL;
      }
      return undefined;
    }
    return undefined;
  };

  return { trClassName, tdClassName };
}

/** Key / value table for parse features (replaces one-cell JSON in compare views). */
function ParseFeatureTable(props: {
  rows: { key: string; value: string }[];
  comfortable?: boolean;
  valueCellClassName?: (rowIndex: number) => string | undefined;
}): React.ReactElement {
  const comfortable = props.comfortable === true;
  if (props.rows.length === 0) {
    return <p className={comfortable ? "p-4 text-base text-slate-500" : "p-3 text-sm text-slate-500"}>No rows.</p>;
  }
  const th = comfortable ? "px-4 py-2.5 text-sm" : "px-3 py-2 text-xs";
  const td = comfortable ? "px-4 py-2.5 text-sm" : "px-3 py-1.5 text-xs";
  return (
    <table className={comfortable ? "min-w-full text-base" : "min-w-full text-sm"}>
      <thead
        className={
          comfortable
            ? "bg-slate-50 text-left text-sm font-medium uppercase text-slate-600"
            : "bg-slate-50 text-left text-xs uppercase text-slate-500"
        }
      >
        <tr>
          <th className={th}>Feature</th>
          <th className={th}>Value</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {props.rows.map((row, rowIndex) => (
          <tr key={row.key}>
            <td className={`${td} font-mono text-slate-800`}>{row.key}</td>
            <td className={cn(`${td} font-mono text-slate-900`, props.valueCellClassName?.(rowIndex))}>
              {row.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Single preview column: parse output + exploded BoM + optional unused-variable callout. */
export type BomPreviewQuerySlice = {
  data?: { rows: unknown[] };
  error: ApiError | null;
  isFetching: boolean;
};

export function BomPreviewColumn(props: {
  columnLabel: string;
  parseQuery: BomPreviewQuerySlice;
  specificQuery: BomPreviewQuerySlice;
  initialRows: UnknownRow[] | undefined;
  comfortable?: boolean;
  /** When set, highlights parse cells that differ from the other column's parse result. */
  compareParse?: { otherRows: unknown[] | undefined; side: "left" | "right" };
  /** When set, highlights BoM rows/cells that differ from the other column's explosion. */
  compareBom?: { otherRows: Record<string, unknown>[] | undefined; side: "left" | "right" };
  /** Pre-computed parse rows (same key order as the other column when comparing). */
  parseFeatureRows?: { key: string; value: string }[] | null;
  /** Highlight parse value cells where the string differs from the partner column (same keys). */
  parsePartnerValuesByKey?: Record<string, string> | null;
  /** When set, use these BoM rows instead of `specificQuery` rows (aligned compare). */
  bomRowsOverride?: Record<string, unknown>[] | null;
  bomTrClassNameOverride?: (rowIndex: number) => string | undefined;
  bomTdClassNameOverride?: (rowIndex: number, column: BomCompareCol) => string | undefined;
  /** Compare panel shows this in a shared row so parse tables stay vertically aligned. */
  suppressIncompleteParseCallout?: boolean;
  /** Softer amber styling for failed previews (side-by-side compare). */
  softPreviewErrors?: boolean;
}): React.ReactElement {
  const comfortable = props.comfortable === true;
  const unused = useMemo(
    () => findUnusedInitial(props.initialRows, props.specificQuery.data?.rows as UnknownRow[] | undefined),
    [props.initialRows, props.specificQuery.data],
  );

  const parseIncompleteNote = useMemo(() => {
    if (props.suppressIncompleteParseCallout === true) {
      return null;
    }
    return extractRemainingSkuTrimmed(props.parseQuery.data?.rows);
  }, [props.parseQuery.data?.rows, props.suppressIncompleteParseCallout]);

  const parseRowsForDisplay = useMemo(() => {
    if (props.parseFeatureRows != null) {
      return props.parseFeatureRows;
    }
    const parsed = extractParseFeatureMap(props.parseQuery.data?.rows);
    if (!parsed) {
      return null;
    }
    return Object.keys(parsed)
      .filter((key) => key !== "remainingSku")
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({ key, value: formatParseFeatureValue(parsed[key]) }));
  }, [props.parseFeatureRows, props.parseQuery.data?.rows]);

  const parseValueCellClassName = useMemo(() => {
    if (!props.parsePartnerValuesByKey || !parseRowsForDisplay) {
      return undefined;
    }
    const partner = props.parsePartnerValuesByKey;
    return (rowIndex: number): string | undefined => {
      const key = parseRowsForDisplay[rowIndex]?.key;
      if (key == null) {
        return undefined;
      }
      const selfVal = parseRowsForDisplay[rowIndex]?.value;
      const partnerVal = partner[key];
      if (partnerVal === undefined) {
        return DIFF_CELL;
      }
      if (selfVal !== partnerVal) {
        return DIFF_CELL;
      }
      return undefined;
    };
  }, [props.parsePartnerValuesByKey, parseRowsForDisplay]);

  const parseHighlight = useMemo(() => {
    if (props.parseFeatureRows != null || parseRowsForDisplay != null) {
      return null;
    }
    if (!props.compareParse) {
      return null;
    }
    return buildParseCompareHighlight(
      props.parseQuery.data?.rows,
      props.compareParse.otherRows,
      props.compareParse.side,
    );
  }, [
    props.parseFeatureRows,
    parseRowsForDisplay,
    props.parseQuery.data?.rows,
    props.compareParse?.otherRows,
    props.compareParse?.side,
    props.compareParse,
  ]);

  const bomHighlight = useMemo(() => {
    if (props.bomRowsOverride != null) {
      return null;
    }
    if (!props.compareBom) {
      return null;
    }
    return buildBomCompareHighlight(
      props.specificQuery.data?.rows as Record<string, unknown>[] | undefined,
      props.compareBom.otherRows,
      props.compareBom.side,
    );
  }, [
    props.bomRowsOverride,
    props.specificQuery.data?.rows,
    props.compareBom?.otherRows,
    props.compareBom?.side,
    props.compareBom,
  ]);

  const bomRowsEffective =
    props.bomRowsOverride ??
    (props.specificQuery.data?.rows as Record<string, unknown>[] | undefined);

  const bomPreviewErrorToShow = useMemo(() => {
    const err = props.specificQuery.error;
    if (!err) {
      return null;
    }
    if (shouldSuppressRedundantBomPreviewError(props.parseQuery.data?.rows, err.message)) {
      return null;
    }
    return err;
  }, [props.specificQuery.error, props.parseQuery.data?.rows]);

  const previewErrorBox =
    props.softPreviewErrors === true
      ? comfortable
        ? "rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-base text-amber-950"
        : "rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
      : null;
  const previewErrorLabel = props.softPreviewErrors === true ? "font-medium" : undefined;

  return (
    <div className={comfortable ? "space-y-4" : "space-y-3"}>
      {props.columnLabel ? (
        <p className={comfortable ? "text-base font-semibold text-slate-800" : "text-sm font-semibold text-slate-800"}>
          {props.columnLabel}
        </p>
      ) : null}
      {props.parseQuery.error ? (
        previewErrorBox ? (
          <p className={previewErrorBox}>
            <span className={previewErrorLabel}>Parse preview: </span>
            {props.parseQuery.error.message}
          </p>
        ) : (
          <p className={comfortable ? "text-base text-rose-700" : "text-sm text-rose-700"}>{props.parseQuery.error.message}</p>
        )
      ) : null}
      {bomPreviewErrorToShow ? (
        previewErrorBox ? (
          <p className={previewErrorBox}>
            <span className={previewErrorLabel}>BoM preview: </span>
            {bomPreviewErrorToShow.message}
          </p>
        ) : (
          <p className={comfortable ? "text-base text-rose-700" : "text-sm text-rose-700"}>
            {bomPreviewErrorToShow.message}
          </p>
        )
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className={comfortable ? "text-lg" : undefined}>Parse (SKU specific)</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {parseIncompleteNote ? (
            <div className={comfortable ? "m-4" : "m-3"}>
              <IncompleteParseCallout remaining={parseIncompleteNote} comfortable={comfortable} />
            </div>
          ) : null}
          {props.parseQuery.isFetching ? (
            <p className={comfortable ? "p-4 text-base text-slate-500" : "p-3 text-sm text-slate-500"}>Loading…</p>
          ) : null}
          {parseRowsForDisplay ? (
            <ParseFeatureTable
              rows={parseRowsForDisplay}
              comfortable={comfortable}
              valueCellClassName={parseValueCellClassName}
            />
          ) : (
            <JsonTable
              rows={props.parseQuery.data?.rows}
              comfortable={comfortable}
              keysOverride={parseHighlight?.keys}
              cellClassName={parseHighlight?.cellClassName}
            />
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className={comfortable ? "text-lg" : undefined}>BoM from rules (create_bom_specific)</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {props.specificQuery.isFetching ? (
            <p className={comfortable ? "p-4 text-base text-slate-500" : "p-3 text-sm text-slate-500"}>Loading…</p>
          ) : null}
          <BomComponentTable
            rows={bomRowsEffective}
            comfortable={comfortable}
            trClassName={props.bomTrClassNameOverride ?? bomHighlight?.trClassName}
            tdClassName={props.bomTdClassNameOverride ?? bomHighlight?.tdClassName}
          />
        </CardBody>
      </Card>
      {unused.length > 0 ? (
        <div
          className={
            comfortable
              ? "rounded-md border border-amber-200 bg-amber-50 p-4 text-base text-amber-950"
              : "rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          }
        >
          <p className="font-medium">Initial BoM variables with no line in the test explosion ({unused.length})</p>
          <ul className="mt-2 list-inside list-disc">
            {unused.map((row) => (
              <li key={String(row.id ?? row.variable_name)}>
                {String(row.variable_name ?? "—")} (initial component: {String(row.component_sku ?? "—")})
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function BomTestPanel(props: {
  testSku: string;
  onTestSku: (value: string) => void;
  onRunTest: () => void;
  running: boolean;
  skuOptions: { value: string; label: string }[];
  parseQuery: { data?: { rows: unknown[] }; error: ApiError | null; isFetching: boolean };
  specificQuery: { data?: { rows: unknown[] }; error: ApiError | null; isFetching: boolean };
  initialRows: UnknownRow[] | undefined;
  /** Larger type and padding for dense tables on wide monitors. */
  density?: "default" | "comfortable";
  /** When true, Run test is disabled (hub read-only). */
  runTestBlocked?: boolean;
  runTestBlockedTitle?: string;
}): React.ReactElement {
  const comfortable = props.density === "comfortable";
  const [skuSearchQuery, setSkuSearchQuery] = useState("");
  const searchableSkuOptions: SearchableSelectOption[] = useMemo(
    () => props.skuOptions.map((option) => ({ value: option.value, label: option.label })),
    [props.skuOptions],
  );
  const skuLabelFallback =
    props.testSku.trim() &&
    !searchableSkuOptions.some((option) => option.value === props.testSku.trim())
      ? props.testSku.trim()
      : null;

  return (
    <div className={comfortable ? "space-y-5" : "space-y-4"}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem] grow">
          <Label className={comfortable ? "text-base" : undefined}>Test SKU (manufacture SKU for this template)</Label>
          <SearchableSelect
            className={
              comfortable
                ? "mt-1.5 block w-full text-base"
                : "mt-1 block w-full text-sm"
            }
            value={props.testSku}
            onValueChange={(option) => props.onTestSku(option?.value ?? "")}
            options={searchableSkuOptions}
            searchValue={skuSearchQuery}
            onSearchChange={setSkuSearchQuery}
            placeholder="Select…"
            searchPlaceholder="Search SKUs…"
            emptyMessage="No SKUs match."
            disabled={props.runTestBlocked}
            selectedLabelOverride={skuLabelFallback}
          />
        </div>
        <ReadOnlyHoverLayer
          active={Boolean(props.runTestBlocked)}
          hoverHint={props.runTestBlockedTitle ?? "Run test is not available."}
        >
          <Button
            type="button"
            size={comfortable ? "md" : "sm"}
            onClick={props.onRunTest}
            disabled={!props.testSku.trim() || props.running || props.runTestBlocked}
            aria-label={props.runTestBlocked ? props.runTestBlockedTitle : undefined}
          >
            {props.running ? "Running…" : "Run test"}
          </Button>
        </ReadOnlyHoverLayer>
      </div>
      <BomPreviewColumn
        columnLabel=""
        parseQuery={props.parseQuery}
        specificQuery={props.specificQuery}
        initialRows={props.initialRows}
        comfortable={comfortable}
      />
    </div>
  );
}

/**
 * Side-by-side preview for approval: baseline revision versus the revision under review.
 */
export function BomCompareTestPanel(props: {
  testSku: string;
  onTestSku: (value: string) => void;
  compareRevision: number | null;
  onCompareRevision: (value: number | null) => void;
  compareRevisionOptions: { value: number; label: string }[];
  skuOptions: { value: string; label: string }[];
  baselineLabel: string;
  /** When false (first revision on template), baseline column shows guidance instead of empty tables. */
  hasBaselineRevision?: boolean;
  proposedLabel: string;
  baselineParse: BomPreviewQuerySlice;
  baselineSpecific: BomPreviewQuerySlice;
  baselineInitialRows: UnknownRow[] | undefined;
  proposedParse: BomPreviewQuerySlice;
  proposedSpecific: BomPreviewQuerySlice;
  proposedInitialRows: UnknownRow[] | undefined;
  density?: "default" | "comfortable";
  runTestBlocked?: boolean;
  runTestBlockedTitle?: string;
}): React.ReactElement {
  const comfortable = props.density === "comfortable";
  const [skuSearchQuery, setSkuSearchQuery] = useState("");
  const searchableSkuOptions: SearchableSelectOption[] = useMemo(
    () => props.skuOptions.map((option) => ({ value: option.value, label: option.label })),
    [props.skuOptions],
  );
  const skuLabelFallback =
    props.testSku.trim() &&
    !searchableSkuOptions.some((option) => option.value === props.testSku.trim())
      ? props.testSku.trim()
      : null;

  const alignedParse = useMemo(() => {
    const leftObj = extractParseFeatureMap(props.baselineParse.data?.rows);
    const rightObj = extractParseFeatureMap(props.proposedParse.data?.rows);
    if (!leftObj && !rightObj) {
      return null;
    }
    const keys = [...new Set([...Object.keys(leftObj ?? {}), ...Object.keys(rightObj ?? {})])]
      .filter((key) => key !== "remainingSku")
      .sort((a, b) => a.localeCompare(b));
    const baselineRows = keys.map((key) => ({ key, value: formatParseFeatureValue(leftObj?.[key]) }));
    const proposedRows = keys.map((key) => ({ key, value: formatParseFeatureValue(rightObj?.[key]) }));
    const partnerForBaseline = Object.fromEntries(proposedRows.map((row) => [row.key, row.value]));
    const partnerForProposed = Object.fromEntries(baselineRows.map((row) => [row.key, row.value]));
    return { baselineRows, proposedRows, partnerForBaseline, partnerForProposed };
  }, [props.baselineParse.data?.rows, props.proposedParse.data?.rows]);

  const alignedBom = useMemo(() => {
    const left = props.baselineSpecific.data?.rows as Record<string, unknown>[] | undefined;
    const right = props.proposedSpecific.data?.rows as Record<string, unknown>[] | undefined;
    if (!left?.length && !right?.length) {
      return null;
    }
    const { left: leftAligned, right: rightAligned } = alignBomRowsForComparison(left, right);
    const highlights = buildAlignedBomHighlights(leftAligned, rightAligned);
    return { leftAligned, rightAligned, ...highlights };
  }, [props.baselineSpecific.data?.rows, props.proposedSpecific.data?.rows]);

  const compareIncompleteRow = useMemo(() => {
    const baselineRemaining = extractRemainingSkuTrimmed(props.baselineParse.data?.rows);
    const proposedRemaining = extractRemainingSkuTrimmed(props.proposedParse.data?.rows);
    if (!baselineRemaining && !proposedRemaining) {
      return null;
    }
    return { baselineRemaining, proposedRemaining };
  }, [props.baselineParse.data?.rows, props.proposedParse.data?.rows]);

  return (
    <div className={comfortable ? "space-y-5" : "space-y-4"}>
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full sm:w-72">
          <Label className={comfortable ? "text-base" : undefined}>Compare against (baseline revision)</Label>
          <select
            className={
              comfortable
                ? "mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base"
                : "mt-1 block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
            }
            value={props.compareRevision ?? ""}
            onChange={(event) => {
              const raw = event.target.value;
              props.onCompareRevision(raw === "" ? null : Number(raw));
            }}
          >
            <option value="">
              {props.compareRevisionOptions.length === 0 ? "First revision — no baseline" : "Select…"}
            </option>
            {props.compareRevisionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-96">
          <Label className={comfortable ? "text-base" : undefined}>Test SKU (manufacture SKU for this template)</Label>
          <SearchableSelect
            className={
              comfortable
                ? "mt-1.5 block w-full text-base"
                : "mt-1 block w-full text-sm"
            }
            value={props.testSku}
            onValueChange={(option) => props.onTestSku(option?.value ?? "")}
            options={searchableSkuOptions}
            searchValue={skuSearchQuery}
            onSearchChange={setSkuSearchQuery}
            placeholder="Select…"
            searchPlaceholder="Search SKUs…"
            emptyMessage="No SKUs match."
            disabled={props.runTestBlocked}
            selectedLabelOverride={skuLabelFallback}
          />
        </div>
      </div>

      {compareIncompleteRow ? (
        <div
          className={
            comfortable
              ? "grid gap-6 md:grid-cols-2 md:gap-8 md:divide-x md:divide-slate-200"
              : "grid gap-5 md:grid-cols-2 md:gap-6 md:divide-x md:divide-slate-200"
          }
        >
          <div className={comfortable ? "min-w-0 md:pr-4" : "min-w-0 md:pr-3"}>
            {compareIncompleteRow.baselineRemaining ? (
              <IncompleteParseCallout remaining={compareIncompleteRow.baselineRemaining} comfortable={comfortable} />
            ) : null}
          </div>
          <div className={comfortable ? "min-w-0 md:pl-4" : "min-w-0 md:pl-3"}>
            {compareIncompleteRow.proposedRemaining ? (
              <IncompleteParseCallout remaining={compareIncompleteRow.proposedRemaining} comfortable={comfortable} />
            ) : null}
          </div>
        </div>
      ) : null}

      <div
        className={
          comfortable
            ? "grid gap-6 md:grid-cols-2 md:gap-8 md:divide-x md:divide-slate-200"
            : "grid gap-5 md:grid-cols-2 md:gap-6 md:divide-x md:divide-slate-200"
        }
      >
        <div className={comfortable ? "min-w-0 md:pr-4" : "min-w-0 md:pr-3"}>
          {props.hasBaselineRevision === false ? (
            <div className={comfortable ? "space-y-4" : "space-y-3"}>
              <p
                className={
                  comfortable ? "text-base font-semibold text-slate-800" : "text-sm font-semibold text-slate-800"
                }
              >
                {props.baselineLabel}
              </p>
              <p
                className={
                  comfortable
                    ? "rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-700"
                    : "rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                }
              >
                This is the first revision on the template, so there is nothing to compare against. Select a test SKU on
                the right to preview parse and BoM output for the revision under review.
              </p>
            </div>
          ) : (
            <BomPreviewColumn
              columnLabel={props.baselineLabel}
              parseQuery={props.baselineParse}
              specificQuery={props.baselineSpecific}
              initialRows={props.baselineInitialRows}
              comfortable={comfortable}
              parseFeatureRows={alignedParse?.baselineRows ?? null}
              parsePartnerValuesByKey={alignedParse?.partnerForBaseline ?? null}
              bomRowsOverride={alignedBom?.leftAligned ?? null}
              bomTrClassNameOverride={alignedBom?.leftTrClassName}
              bomTdClassNameOverride={alignedBom?.leftTdClassName}
              suppressIncompleteParseCallout
              softPreviewErrors
            />
          )}
        </div>
        <div className={comfortable ? "min-w-0 md:pl-4" : "min-w-0 md:pl-3"}>
          <BomPreviewColumn
            columnLabel={props.proposedLabel}
            parseQuery={props.proposedParse}
            specificQuery={props.proposedSpecific}
            initialRows={props.proposedInitialRows}
            comfortable={comfortable}
            parseFeatureRows={alignedParse?.proposedRows ?? null}
            parsePartnerValuesByKey={alignedParse?.partnerForProposed ?? null}
            bomRowsOverride={alignedBom?.rightAligned ?? null}
            bomTrClassNameOverride={alignedBom?.rightTrClassName}
            bomTdClassNameOverride={alignedBom?.rightTdClassName}
            suppressIncompleteParseCallout
            softPreviewErrors
          />
        </div>
      </div>
    </div>
  );
}

/** Generic object-array table (keys from the first row). Used for parse preview output and read-only rule dumps. */
export function JsonTable({
  rows,
  comfortable,
  keysOverride,
  cellClassName,
  omitKeys,
}: {
  rows: unknown[] | undefined;
  comfortable?: boolean;
  /** When comparing two parses, union of keys so columns align. */
  keysOverride?: string[];
  cellClassName?: (rowIndex: number, key: string) => string | undefined;
  /** Column keys to hide (matched case-insensitively on header name). */
  omitKeys?: string[];
}): React.ReactElement {
  if (!rows || rows.length === 0) {
    return <p className={comfortable ? "p-4 text-base text-slate-500" : "p-3 text-sm text-slate-500"}>No rows.</p>;
  }
  const first = rows[0];
  if (!first || typeof first !== "object") {
    return <p className={comfortable ? "p-4 text-base text-slate-500" : "p-3 text-sm text-slate-500"}>No data.</p>;
  }
  const omit = new Set((omitKeys ?? []).map((key) => key.toLowerCase()));
  const rawKeys =
    keysOverride && keysOverride.length > 0 ? keysOverride : Object.keys(first as object);
  const keys = rawKeys.filter((key) => !omit.has(key.toLowerCase()));
  if (keys.length === 0) {
    return <p className={comfortable ? "p-4 text-base text-slate-500" : "p-3 text-sm text-slate-500"}>No columns.</p>;
  }
  const baseTd = comfortable ? "px-4 py-2.5 font-mono text-sm leading-snug" : "px-3 py-1.5 font-mono text-xs";
  return (
    <table className={comfortable ? "min-w-full text-base" : "min-w-full text-sm"}>
      <thead
        className={
          comfortable
            ? "bg-slate-50 text-left text-sm font-medium uppercase text-slate-600"
            : "bg-slate-50 text-left text-xs uppercase text-slate-500"
        }
      >
        <tr>
          {keys.map((key) => (
            <th key={key} className={comfortable ? "px-4 py-2.5" : "px-3 py-2"}>
              {key}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row, i) => (
          <tr key={i}>
            {keys.map((key) => (
              <td key={key} className={cn(baseTd, cellClassName?.(i, key))}>
                {formatCell((row as Record<string, unknown>)[key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatCell(v: unknown): string {
  return formatCellRaw(v);
}

function BomComponentTable({
  rows,
  comfortable,
  trClassName,
  tdClassName,
}: {
  rows: Record<string, unknown>[] | undefined;
  comfortable?: boolean;
  trClassName?: (rowIndex: number) => string | undefined;
  tdClassName?: (rowIndex: number, col: BomCompareCol) => string | undefined;
}): React.ReactElement {
  if (!rows || rows.length === 0) {
    return <p className={comfortable ? "p-4 text-base text-slate-500" : "p-3 text-sm text-slate-500"}>No rows.</p>;
  }
  const th = comfortable ? "px-4 py-2.5 text-sm" : "px-3 py-2 text-xs";
  const td = comfortable
    ? "px-4 py-2.5 text-sm"
    : "px-3 py-1.5 text-xs";
  return (
    <table
      className={
        comfortable
          ? "w-full min-w-full table-fixed text-base"
          : "w-full min-w-full table-fixed text-sm"
      }
    >
      <thead
        className={
          comfortable
            ? "bg-slate-50 text-left text-sm font-medium uppercase text-slate-600"
            : "bg-slate-50 text-left text-xs uppercase text-slate-500"
        }
      >
        <tr>
          <th className={`${th} w-[30%]`}>Component SKU</th>
          <th className={`${th} w-[10%] text-right`}>Quantity</th>
          <th className={`${th} w-[18%]`}>Variable</th>
          <th className={th}>Description</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row, i) => (
          <tr key={i} className={trClassName?.(i)}>
            <td className={cn(`${td} align-top font-mono`, tdClassName?.(i, "component_sku"))}>
              {String((row as Record<string, unknown>).component_sku ?? "—")}
            </td>
            <td className={cn(`${td} align-top text-right font-mono`, tdClassName?.(i, "quantity"))}>
              {(() => {
                const q = (row as Record<string, unknown>).quantity;
                const n = typeof q === "number" ? q : Number(q);
                return Number.isFinite(n) ? formatNumber(n) : "—";
              })()}
            </td>
            <td className={cn(`${td} align-top`, tdClassName?.(i, "variable_name"))}>
              {String((row as Record<string, unknown>).variable_name ?? "—")}
            </td>
            <td
              className={cn(
                `${td} align-top overflow-hidden whitespace-nowrap text-slate-600`,
                tdClassName?.(i, "full_description"),
              )}
            >
              {(() => {
                const description = String((row as Record<string, unknown>).full_description ?? "—");
                return (
                  <HoverHintContent hoverHint={description} block cursor="default" className="max-w-full">
                    <span className="block max-w-full truncate">{description}</span>
                  </HoverHintContent>
                );
              })()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RevisionLockedBanner({
  approved,
  submitted,
}: {
  approved: boolean;
  submitted: boolean;
}): React.ReactElement | null {
  if (approved) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-800">
        This revision is <strong>approved</strong>. Open another revision to make changes, or start a new revision from the
        template.
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
        This revision is <strong>submitted for approval</strong>. Editing is locked until it is approved or you work on a
        new draft revision.
      </div>
    );
  }
  return null;
}

export type BomEditableColumnDef = {
  key: string;
  label: string;
  width?: string;
  /** When set, the column uses a searchable combo box (type to filter by label) instead of a plain text field. */
  selectOptions?: { value: string; label: string }[];
  /** When `selectOptions` is used, show the list loading state until the first options payload arrives. */
  selectOptionsLoading?: boolean;
  /** Minimum width of the portaled station dropdown when this column uses `selectOptions`. */
  selectPanelMinWidth?: number;
  /** Shown next to the column label (header and add-row label) as a help control. */
  headerHelpText?: string;
};

/** In-progress table row edit: draft overlays the saved row for merge (`{ ...row, ...draft }`). */
export type EditableKeyValueEditing = { id: number; draft: Record<string, unknown> } | null;

function editableColumnWidthStyle(width?: string): { width: string } | undefined {
  return width ? { width } : undefined;
}

export function EditableKeyValueTable(props: {
  title: string;
  rows: Record<string, unknown>[];
  columns: BomEditableColumnDef[];
  isEditable: boolean;
  onSave: (row: Record<string, unknown>) => Promise<void>;
  onAdd: (draft: Record<string, unknown>) => Promise<void>;
  newRowTemplate: Record<string, unknown>;
  density?: "default" | "comfortable";
  trailingColumn?: {
    label: string;
    render: (context: {
      row: Record<string, unknown>;
      mergedRow: Record<string, unknown>;
      rows: Record<string, unknown>[];
      editing: EditableKeyValueEditing;
      comfortable: boolean;
    }) => ReactNode;
  };
}): React.ReactElement {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState<number | "new" | null>(null);
  const [editing, setEditing] = useState<EditableKeyValueEditing>(null);
  const comfortable = props.density === "comfortable";

  const startAdd = (): void => {
    setAdding(true);
    setDraft({ ...props.newRowTemplate });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className={comfortable ? "text-lg" : undefined}>{props.title}</CardTitle>
          {props.isEditable ? (
            <Button type="button" size={comfortable ? "md" : "sm"} variant="secondary" onClick={startAdd} disabled={adding}>
              Add row
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="overflow-x-auto p-0">
        {adding ? (
          <div className={comfortable ? "border-b border-slate-200 p-4" : "border-b border-slate-200 p-3"}>
            <p
              className={comfortable ? "mb-3 text-sm font-medium text-slate-600" : "mb-2 text-xs font-medium text-slate-600"}
            >
              New row
            </p>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {props.columns.map((col) => (
                <div key={col.key}>
                  <div className={comfortable ? "mb-0.5 flex items-center gap-1" : "mb-0.5 flex items-center gap-1"}>
                    <Label className={comfortable ? "text-sm" : undefined}>{col.label}</Label>
                    {col.headerHelpText ? (
                      <HoverHintTrigger
                        align="center"
                        trigger={
                          <button
                            type="button"
                            className="inline-flex rounded p-0.5 text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                            aria-label={`Help: ${col.label}`}
                          >
                            <span className="text-xs font-semibold leading-none">?</span>
                          </button>
                        }
                      >
                        <span className="whitespace-normal font-normal normal-case">{col.headerHelpText}</span>
                      </HoverHintTrigger>
                    ) : null}
                  </div>
                  <FieldInput
                    value={draft[col.key]}
                    onChange={(v) => setDraft((d) => ({ ...d, [col.key]: v }))}
                    bool={col.key === "multiple" || col.key === "strip"}
                    comfortable={comfortable}
                    selectOptions={col.selectOptions}
                    selectOptionsLoading={col.selectOptionsLoading}
                    selectPanelMinWidth={col.selectPanelMinWidth}
                  />
                </div>
              ))}
              {props.trailingColumn ? (
                <div>
                  <Label className={comfortable ? "text-sm" : undefined}>{props.trailingColumn.label}</Label>
                  <p className={comfortable ? "mt-1.5 text-sm text-slate-500" : "mt-1 text-xs text-slate-500"}>—</p>
                </div>
              ) : null}
            </div>
            <div className="mt-2 inline-flex items-center gap-0.5">
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    size={comfortable ? "md" : "sm"}
                    className={cn(
                      "min-w-0 border-transparent p-0 !bg-emerald-600 !text-white hover:!bg-emerald-700 focus:!ring-emerald-500 disabled:!bg-emerald-600/60",
                      comfortable ? "h-9 w-9" : "h-7 w-7",
                    )}
                    aria-label="Save"
                    onClick={async () => {
                      setSaving("new");
                      try {
                        await props.onAdd(draft);
                        setAdding(false);
                        setDraft({});
                      } finally {
                        setSaving(null);
                      }
                    }}
                    disabled={saving === "new"}
                  >
                    <Check className={comfortable ? "h-4 w-4" : "h-3.5 w-3.5"} strokeWidth={2.5} />
                  </Button>
                }
              >
                <span>Save</span>
              </HoverHintTrigger>
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    size={comfortable ? "md" : "sm"}
                    variant="secondary"
                    className={cn(
                      "min-w-0 border-rose-200 p-0 text-rose-600 hover:bg-rose-50",
                      comfortable ? "h-9 w-9" : "h-7 w-7",
                    )}
                    aria-label="Cancel"
                    onClick={() => {
                      setAdding(false);
                    }}
                  >
                    <X className={comfortable ? "h-4 w-4" : "h-3.5 w-3.5"} strokeWidth={2.5} />
                  </Button>
                }
              >
                <span>Cancel</span>
              </HoverHintTrigger>
            </div>
          </div>
        ) : null}
        <table className={comfortable ? "w-full min-w-full table-fixed text-base" : "w-full min-w-full table-fixed text-sm"}>
          <thead
            className={
              comfortable
                ? "bg-slate-50 text-left text-sm font-medium uppercase text-slate-600"
                : "bg-slate-50 text-left text-xs uppercase text-slate-500"
            }
          >
            <tr>
              {props.columns.map((c) => (
                <th
                  key={c.key}
                  className={comfortable ? "px-4 py-2.5" : "px-3 py-2"}
                  style={editableColumnWidthStyle(c.width)}
                >
                  <span className="inline-flex items-center gap-1">
                    <span>{c.label}</span>
                    {c.headerHelpText ? (
                      <HoverHintTrigger
                        align="center"
                        trigger={
                          <button
                            type="button"
                            className="inline-flex rounded p-0.5 text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                            aria-label={`Help: ${c.label}`}
                          >
                            <span className="text-xs font-semibold leading-none">?</span>
                          </button>
                        }
                      >
                        <span className="whitespace-normal font-normal normal-case">{c.headerHelpText}</span>
                      </HoverHintTrigger>
                    ) : null}
                  </span>
                </th>
              ))}
              {props.trailingColumn ? (
                <th className={comfortable ? "px-4 py-2.5" : "px-3 py-2"}>
                  {props.trailingColumn.label}
                </th>
              ) : null}
              {props.isEditable ? (
                <th className={comfortable ? "px-4 py-2.5 text-right" : "px-3 py-2 text-right"}>Actions</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => (
              <EditableRow
                key={String(row.id)}
                row={row}
                allRows={props.rows}
                columns={props.columns}
                isEditable={props.isEditable}
                onSave={props.onSave}
                saving={saving}
                setSaving={setSaving}
                comfortable={comfortable}
                editing={editing}
                setEditing={setEditing}
                trailingColumn={props.trailingColumn}
              />
            ))}
          </tbody>
        </table>
        {props.rows.length === 0 && !adding ? (
          <p className={comfortable ? "p-5 text-base text-slate-500" : "p-4 text-sm text-slate-500"}>No rows yet.</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function EditableRow(props: {
  row: Record<string, unknown>;
  allRows: Record<string, unknown>[];
  columns: BomEditableColumnDef[];
  isEditable: boolean;
  onSave: (row: Record<string, unknown>) => Promise<void>;
  saving: number | "new" | null;
  setSaving: (v: number | "new" | null) => void;
  comfortable: boolean;
  editing: EditableKeyValueEditing;
  setEditing: Dispatch<SetStateAction<EditableKeyValueEditing>>;
  trailingColumn?: {
    label: string;
    render: (context: {
      row: Record<string, unknown>;
      mergedRow: Record<string, unknown>;
      rows: Record<string, unknown>[];
      editing: EditableKeyValueEditing;
      comfortable: boolean;
    }) => ReactNode;
  };
}): React.ReactElement {
  const id = Number(props.row.id);
  const isEditing = props.editing != null && Number(props.editing.id) === id;
  const mergedRow =
    isEditing && props.editing
      ? { ...props.row, ...props.editing.draft }
      : props.row;

  if (!isEditing) {
    return (
      <tr className="border-t border-slate-100">
        {props.columns.map((c) => (
          <td
            key={c.key}
            className={
              props.comfortable
                ? "px-4 py-2.5 align-top text-sm leading-relaxed"
                : "px-3 py-2 align-top text-xs"
            }
            style={editableColumnWidthStyle(c.width)}
          >
            {c.selectOptions != null
              ? formatSelectDisplay(c.selectOptions, props.row[c.key])
              : formatCellDisplay(props.row[c.key])}
          </td>
        ))}
        {props.trailingColumn ? (
          <td
            className={
              props.comfortable
                ? "px-4 py-2.5 align-top text-sm leading-relaxed"
                : "px-3 py-2 align-top text-xs"
            }
          >
            {props.trailingColumn.render({
              row: props.row,
              mergedRow,
              rows: props.allRows,
              editing: props.editing,
              comfortable: props.comfortable,
            })}
          </td>
        ) : null}
        {props.isEditable ? (
          <td className={props.comfortable ? "px-4 py-2.5 text-right" : "px-3 py-2 text-right"}>
            <Button
              type="button"
              size={props.comfortable ? "md" : "sm"}
              variant="secondary"
              onClick={() => {
                props.setEditing({ id, draft: {} });
              }}
            >
              Edit
            </Button>
          </td>
        ) : null}
      </tr>
    );
  }
  return (
    <tr className="border-t border-slate-100 bg-brand-50/30">
      {props.columns.map((c) => (
        <td
          key={c.key}
          className={props.comfortable ? "px-3 py-2 align-top" : "px-2 py-2 align-top"}
          style={editableColumnWidthStyle(c.width)}
        >
          <FieldInput
            value={mergedRow[c.key]}
            onChange={(v) => {
              props.setEditing((prev) => {
                if (prev == null || Number(prev.id) !== id) {
                  return prev;
                }
                return { id, draft: { ...prev.draft, [c.key]: v } };
              });
            }}
            bool={c.key === "multiple" || c.key === "strip"}
            comfortable={props.comfortable}
            selectOptions={c.selectOptions}
            selectOptionsLoading={c.selectOptionsLoading}
            selectPanelMinWidth={c.selectPanelMinWidth}
          />
        </td>
      ))}
      {props.trailingColumn ? (
        <td
          className={
            props.comfortable
              ? "px-3 py-2 align-top text-sm leading-relaxed"
              : "px-2 py-2 align-top text-xs"
          }
        >
          {props.trailingColumn.render({
            row: props.row,
            mergedRow,
            rows: props.allRows,
            editing: props.editing,
            comfortable: props.comfortable,
          })}
        </td>
      ) : null}
      <td
        className={
          props.comfortable
            ? "whitespace-nowrap px-3 py-2 text-right"
            : "whitespace-nowrap px-2 py-2 text-right"
        }
      >
        <span className="inline-flex items-center gap-0.5">
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                size={props.comfortable ? "md" : "sm"}
                className={cn(
                  "min-w-0 shrink-0 border-transparent p-0 !bg-emerald-600 !text-white hover:!bg-emerald-700 focus:!ring-emerald-500 disabled:!bg-emerald-600/60",
                  props.comfortable ? "h-9 w-9" : "h-7 w-7",
                )}
                aria-label="Save"
                onClick={async () => {
                  props.setSaving(id);
                  try {
                    await props.onSave(mergedRow);
                    props.setEditing(null);
                  } finally {
                    props.setSaving(null);
                  }
                }}
                disabled={props.saving === id}
              >
                <Check
                  className={props.comfortable ? "h-4 w-4" : "h-3.5 w-3.5"}
                  strokeWidth={2.5}
                />
              </Button>
            }
          >
            <span>Save</span>
          </HoverHintTrigger>
          <HoverHintTrigger
            trigger={
              <Button
                type="button"
                size={props.comfortable ? "md" : "sm"}
                variant="secondary"
                className={cn(
                  "min-w-0 shrink-0 border-rose-200 p-0 text-rose-600 hover:bg-rose-50",
                  props.comfortable ? "h-9 w-9" : "h-7 w-7",
                )}
                aria-label="Cancel"
                onClick={() => {
                  props.setEditing(null);
                }}
              >
                <X
                  className={props.comfortable ? "h-4 w-4" : "h-3.5 w-3.5"}
                  strokeWidth={2.5}
                />
              </Button>
            }
          >
            <span>Cancel</span>
          </HoverHintTrigger>
        </span>
      </td>
    </tr>
  );
}

const KIT_STATION_EMPTY_OPTION: SearchableSelectOption = { value: "", label: "— (no kit station)" };

function filterSelectOptionsForColumn(
  options: { value: string; label: string }[],
  query: string,
): { value: string; label: string }[] {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) {
    return options;
  }
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(normalized) || option.value.toLowerCase().includes(normalized),
  );
}

function formatSelectDisplay(options: { value: string; label: string }[], value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const s = String(value);
  const hit = options.find((option) => option.value === s);
  return hit != null ? hit.label : s;
}

function formatCellDisplay(v: unknown): string {
  if (v === null || v === undefined) {
    return "—";
  }
  if (typeof v === "boolean") {
    return v ? "yes" : "no";
  }
  if (typeof v === "number") {
    return formatNumber(v);
  }
  return String(v);
}

function SearchableSelectFieldInput(props: {
  value: unknown;
  onChange: (v: unknown) => void;
  options: { value: string; label: string }[];
  isLoading?: boolean;
  panelMinWidth?: number;
}): React.ReactElement {
  const [search, setSearch] = useState("");
  const filtered = useMemo(
    () => filterSelectOptionsForColumn(props.options, search),
    [props.options, search],
  );
  const dropdownOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query.length === 0 || KIT_STATION_EMPTY_OPTION.label.toLowerCase().includes(query)) {
      return [KIT_STATION_EMPTY_OPTION, ...filtered];
    }
    return filtered;
  }, [filtered, search]);

  const strValue =
    props.value === undefined || props.value === null || props.value === "" ? "" : String(props.value);

  return (
    <SearchableSelect
      className="mt-1 w-full min-w-[11rem]"
      panelMinWidth={props.panelMinWidth ?? 260}
      value={strValue}
      onValueChange={(option) => {
        props.onChange(option == null || option.value === "" ? null : Number(option.value));
      }}
      options={dropdownOptions}
      searchValue={search}
      onSearchChange={setSearch}
      placeholder="Select station…"
      searchPlaceholder="Type station name…"
      emptyMessage={props.isLoading ? "Loading stations…" : "No matching station."}
      isLoading={Boolean(props.isLoading)}
      selectedLabelOverride={
        strValue !== "" && !props.options.some((option) => option.value === strValue) ? `Station #${strValue}` : null
      }
    />
  );
}

function FieldInput(props: {
  value: unknown;
  onChange: (v: unknown) => void;
  bool?: boolean;
  comfortable?: boolean;
  selectOptions?: { value: string; label: string }[];
  selectOptionsLoading?: boolean;
  selectPanelMinWidth?: number;
}): React.ReactElement {
  if (props.selectOptions != null) {
    return (
      <SearchableSelectFieldInput
        value={props.value}
        onChange={props.onChange}
        options={props.selectOptions}
        isLoading={props.selectOptionsLoading}
        panelMinWidth={props.selectPanelMinWidth}
      />
    );
  }
  if (props.bool) {
    return (
      <input
        type="checkbox"
        className={props.comfortable ? "mt-2 h-5 w-5 rounded border-slate-300" : "mt-2 h-4 w-4 rounded border-slate-300"}
        checked={Boolean(props.value)}
        onChange={(event) => props.onChange(event.target.checked)}
      />
    );
  }
  if (typeof props.value === "number" || props.value === undefined || typeof props.value === "string") {
    return (
      <Input
        className={props.comfortable ? "h-10 text-sm" : "text-xs"}
        value={props.value === undefined || props.value === null ? "" : String(props.value)}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === "") {
            props.onChange("");
            return;
          }
          if (/^-?\d+(\.\d+)?$/.test(raw) && !raw.startsWith("0x")) {
            const n = Number(raw);
            if (!Number.isNaN(n)) {
              props.onChange(n);
              return;
            }
          }
          props.onChange(raw);
        }}
      />
    );
  }
  return <Input className={props.comfortable ? "text-sm" : "text-xs"} value={JSON.stringify(props.value)} onChange={() => {}} readOnly />;
}
